import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Footer from '../components/Footer';

function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await api.get('/keys');
      setApiKeys(response.data.apiKeys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await api.post('/keys', { name: newKeyName || 'New Key' });
      setNewlyCreatedKey(response.data.apiKey);
      setApiKeys([response.data.apiKey, ...apiKeys]);
      setNewKeyName('');
      setShowNewKeyForm(false);
      toast.success('API key created!');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const deleteApiKey = async (id) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/keys/${id}`);
      setApiKeys(apiKeys.filter(key => key.id !== id));
      toast.success('API key deleted successfully');
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const maskApiKey = (key) => {
    // Show first 15 chars and last 4 chars
    if (key.length <= 19) return key;
    return `${key.substring(0, 15)}...${key.substring(key.length - 4)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <>
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">API Keys</h1>
          <p className="text-gray-600 mt-2">Manage your API keys for authentication</p>
        </div>
        <button
          onClick={() => setShowNewKeyForm(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 w-full sm:w-auto whitespace-nowrap"
        >
          Create New Key
        </button>
      </div>

      {/* New Key Alert - SHOW ONCE */}
      {newlyCreatedKey && (
        <div className="bg-green-50 border-2 border-green-400 rounded-lg p-6 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-green-900 font-bold text-lg mb-2">⚠️ API Key Created - Copy Now!</h3>
              <p className="text-green-800 text-sm mb-4">
                <strong>Make sure to copy your API key now. You won't be able to see it again!</strong> For security reasons, we can only show the full key once.
              </p>
              <div className="bg-white rounded-lg p-4 mb-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <code className="font-mono text-sm break-all text-gray-900 font-semibold flex-1">{newlyCreatedKey.key}</code>
                  <button
                    onClick={() => copyToClipboard(newlyCreatedKey.key)}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium whitespace-nowrap w-full sm:w-auto"
                  >
                    Copy Key
                  </button>
                </div>
              </div>
              <button
                onClick={() => setNewlyCreatedKey(null)}
                className="text-sm text-green-700 hover:text-green-600 font-medium"
              >
                I've copied my key, dismiss this message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Key Form */}
      {showNewKeyForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Create New API Key</h2>
          <form onSubmit={createApiKey}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Key Name (Optional)
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production Key, Development Key"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={creating}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Key'}
              </button>
              <button
                type="button"
                onClick={() => setShowNewKeyForm(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {apiKeys.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 mb-4">No API keys yet</p>
            <button
              onClick={() => setShowNewKeyForm(true)}
              className="text-indigo-600 hover:text-indigo-500 font-medium"
            >
              Create your first API key
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {apiKeys.map((key) => {
              const isNewKey = newlyCreatedKey && newlyCreatedKey.id === key.id;
              const displayKey = isNewKey ? key.key : maskApiKey(key.key);
              
              return (
                <div key={key.id} className="p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{key.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Created {new Date(key.createdAt).toLocaleDateString()}
                      </p>
                      {key.lastUsedAt && (
                        <p className="text-sm text-gray-500">
                          Last used {new Date(key.lastUsedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteApiKey(key.id)}
                      className="text-red-600 hover:text-red-500 text-sm font-medium self-end sm:self-start"
                    >
                      Delete
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded p-3 font-mono text-sm break-all flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <span className={`${isNewKey ? 'text-gray-900 font-semibold' : 'text-gray-600'} break-all`}>
                      {displayKey}
                    </span>
                    <button
                      onClick={() => copyToClipboard(key.key)}
                      className="text-indigo-600 hover:text-indigo-500 text-sm font-medium whitespace-nowrap self-end sm:self-auto"
                      disabled={!isNewKey}
                    >
                      {isNewKey ? 'Copy' : 'Hidden'}
                    </button>
                  </div>
                  {!isNewKey && (
                    <p className="text-xs text-gray-500 mt-2">
                      🔒 For security, we only show the full key once after creation
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>

    <Footer className="hidden md:block" />
    </>
  );
}

export default ApiKeys;